// Origens do Gestão autorizadas a incorporar a área do organizador
// (VITE_ORGANIZER_PARENT_ORIGINS, lista JSON definida NO BUILD).
//
// Uma lista só decide as duas pontas: de quem o iframe aceita mensagens
// (handshake) e quem pode emoldurá-lo (`frame-ancestors`, gerado no build em
// `_headers`). Só vale origem EXATA — esquema, host e porta, sem caminho, barra
// final, curinga ou credencial — e HTTP apenas em localhost. Ausente ou
// inválida, a área não conversa com ninguém: nunca "qualquer origem".
//
// Módulo puro (sem DOM): também é importado pelo vite.config.ts.

export type ParentOriginsConfig =
  | { status: 'ok'; origins: readonly string[] }
  | { status: 'missing' }
  | { status: 'invalid'; reason: string };

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
// Hostname já normalizado pelo URL (minúsculo, IDN em punycode).
const HOSTNAME = /^[a-z0-9-]+(\.[a-z0-9-]+)*$/;

function invalid(reason: string): ParentOriginsConfig {
  return { status: 'invalid', reason };
}

function isExactOrigin(entry: string): boolean {
  let url: URL;
  try {
    url = new URL(entry);
  } catch {
    return false;
  }
  if (url.origin !== entry) return false;
  if (url.protocol === 'https:') return HOSTNAME.test(url.hostname);
  return url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname);
}

export function parseParentOrigins(raw: string | null | undefined): ParentOriginsConfig {
  if (raw === undefined || raw === null || raw.trim() === '') return { status: 'missing' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return invalid('não é JSON');
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return invalid('esperada uma lista JSON não vazia de origens');
  }

  const origins: string[] = [];
  for (const entry of parsed) {
    if (typeof entry !== 'string' || !isExactOrigin(entry)) {
      return invalid(`${JSON.stringify(entry)} não é uma origem exata permitida`);
    }
    if (origins.includes(entry)) return invalid(`${entry} repetida`);
    origins.push(entry);
  }
  return { status: 'ok', origins };
}

/**
 * Conteúdo do `_headers` do Cloudflare Pages. `frame-ancestors` só vale por
 * header HTTP (em `<meta>` é ignorado). Sem configuração válida, a área do
 * organizador não pode ser incorporada por ninguém.
 */
export function buildOrganizerHeadersFile(config: ParentOriginsConfig): string {
  const sources = config.status === 'ok' ? config.origins.join(' ') : "'none'";
  return [
    '# Gerado no build a partir de VITE_ORGANIZER_PARENT_ORIGINS (ver vite.config.ts).',
    '# Só a área do organizador pode ser incorporada, e só pelas origens do Gestão.',
    '/organizer/*',
    `  Content-Security-Policy: frame-ancestors ${sources}`,
    '',
  ].join('\n');
}
